/**
 * Whether a WebAuthn ceremony ended without the user completing it.
 *
 * A dismissed OS prompt is a decision, not a failure — callers return to whatever
 * they were showing rather than reporting an error. The browser deliberately
 * reports a cancel and a timeout as the same `NotAllowedError` so a site cannot
 * tell which credentials a user holds, so both are treated as "not completed";
 * `AbortError` covers a ceremony a newer one superseded.
 *
 * `@simplewebauthn/browser` re-wraps both as a `WebAuthnError` whose `name` is
 * inherited from the original DOMException, so the `cause` check is only a
 * backstop for an error that reaches us unwrapped.
 *
 * (RP ID resolution lives in `webauthn-rp.ts`; encryption-passkey failures have
 * their own mapping in `identity-crypto-errors.ts`.)
 */
export function isCancelledWebAuthnCeremony(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const names = [error.name, (error.cause as Error | undefined)?.name];
    return names.some(name => name === 'NotAllowedError' || name === 'AbortError');
}
